import React, { useState } from "react";
import Card from "./Card";

const TwoFASetup = ({ user, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [qrCode, setQRCode] = useState("");
  const [secret, setSecret] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [verifyToken, setVerifyToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [codesCopied, setCodesCopied] = useState(false);

  const generateQR = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        "http://localhost:5000/api/auth/2fa/enable",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (data.success) {
        setQRCode(data.qrCode);
        setSecret(data.secret);
        setRecoveryCodes(data.recoveryCodes);
        setStep(2);
      } else {
        setError(data.error || "Failed to generate 2FA secret");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (verifyToken.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        "http://localhost:5000/api/auth/2fa/verify",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ token: verifyToken }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setStep(3);
        setTimeout(() => onSuccess(), 2000);
      } else {
        setError(data.error || "Verification failed. Please check your code.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyRecoveryCodes = () => {
    const codesText = recoveryCodes.join("\n");
    navigator.clipboard.writeText(codesText);
    setCodesCopied(true);
    setTimeout(() => setCodesCopied(false), 2000);
  };

  const downloadRecoveryCodes = () => {
    const codesText = `2FA Recovery Codes for ${user.email}\nGenerated: ${new Date().toLocaleString()}\n\n${recoveryCodes.join("\n")}\n\n⚠️ Keep these codes safe! Each can only be used once.`;
    const blob = new Blob([codesText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "2FA-Recovery-Codes.txt";
    a.click();
  };

  return (
    <Card>
      {/* Step 1: Introduction */}
      {step === 1 && (
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-24 h-24 rounded-full bg-gradient-to-r from-[#22c55e] to-[#16a34a] flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-12 h-12 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>

          <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Enable Two-Factor Authentication
          </h2>

          <p className="text-[#A1A1AA] text-lg mb-8">
            Add an extra layer of security to your account. You'll need a code
            from your phone to sign in.
          </p>

          {/* Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="p-4 bg-surface-soft rounded-xl border border-[#1F2937]">
              <div className="text-3xl mb-2">🔒</div>
              <h3 className="font-semibold text-[var(--text-primary)] mb-1">
                Enhanced Security
              </h3>
              <p className="text-sm text-[#A1A1AA]">
                Protect against unauthorized access
              </p>
            </div>
            <div className="p-4 bg-surface-soft rounded-xl border border-[#1F2937]">
              <div className="text-3xl mb-2">📱</div>
              <h3 className="font-semibold text-[var(--text-primary)] mb-1">Easy to Use</h3>
              <p className="text-sm text-[#A1A1AA]">
                Works with any authenticator app
              </p>
            </div>
            <div className="p-4 bg-surface-soft rounded-xl border border-[#1F2937]">
              <div className="text-3xl mb-2">💰</div>
              <h3 className="font-semibold text-[var(--text-primary)] mb-1">
                Secure Withdrawals
              </h3>
              <p className="text-sm text-[#A1A1AA]">
                Extra protection for your funds
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border-2 border-red-500 rounded-xl">
              <p className="text-red-500 font-medium">{error}</p>
            </div>
          )}

          <button
            onClick={generateQR}
            disabled={loading}
            className="px-8 py-4 rounded-xl bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-bold text-lg hover:shadow-lg hover:shadow-primary/50 transition-all disabled:opacity-50"
          >
            {loading ? "Generating..." : "Get Started"}
          </button>
        </div>
      )}

      {/* Step 2: QR Code & Verification */}
      {step === 2 && (
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent text-center">
            Set Up Your Authenticator
          </h2>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* QR Code Section */}
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
                1. Scan QR Code
              </h3>
              <div className="bg-white p-4 rounded-xl mb-4">
                <img src={qrCode} alt="2FA QR Code" className="w-full" />
              </div>
              <div className="p-3 bg-surface-soft rounded-lg border border-[#1F2937]">
                <p className="text-xs text-[#A1A1AA] mb-2">
                  Or enter this code manually:
                </p>
                <code className="text-primary text-sm font-mono break-all">
                  {secret}
                </code>
              </div>
            </div>

            {/* Recovery Codes Section */}
            <div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
                2. Save Recovery Codes
              </h3>
              <div className="bg-surface-soft p-4 rounded-xl border-2 border-primary/30 mb-4">
                <div className="flex items-start gap-2 mb-3">
                  <svg
                    className="w-5 h-5 text-primary flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div>
                    <p className="font-semibold text-[var(--text-primary)] text-sm">
                      Keep these codes safe!
                    </p>
                    <p className="text-xs text-[#A1A1AA]">
                      Each code can only be used once
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3 max-h-48 overflow-y-auto">
                  {recoveryCodes.map((code, i) => (
                    <div
                      key={i}
                      className="bg-surface-hover px-3 py-2 rounded text-center"
                    >
                      <code className="text-primary font-mono text-sm">
                        {code}
                      </code>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={copyRecoveryCodes}
                    className="flex-1 px-4 py-2 rounded-lg bg-[var(--bg-card)] text-primary text-sm font-semibold hover:bg-[#374151] transition-all"
                  >
                    {codesCopied ? "✓ Copied!" : "📋 Copy"}
                  </button>
                  <button
                    onClick={downloadRecoveryCodes}
                    className="flex-1 px-4 py-2 rounded-lg bg-[var(--bg-card)] text-primary text-sm font-semibold hover:bg-[#374151] transition-all"
                  >
                    💾 Download
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Verification Input */}
          <div className="max-w-md mx-auto">
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4 text-center">
              3. Verify Setup
            </h3>
            <p className="text-[#A1A1AA] text-sm mb-4 text-center">
              Enter the 6-digit code from your authenticator app:
            </p>
            <input
              type="text"
              value={verifyToken}
              onChange={(e) =>
                setVerifyToken(e.target.value.replace(/\D/g, ""))
              }
              placeholder="000000"
              maxLength="6"
              className="w-full px-4 py-4 rounded-xl bg-surface-soft border-2 border-primary text-primary text-center text-3xl font-bold tracking-[0.3em] focus:outline-none focus:ring-4 focus:ring-[#22c55e]/20 transition-all mb-4 placeholder:text-[var(--text-secondary)]/40"
            />

            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border-2 border-red-500 rounded-lg">
                <p className="text-red-500 text-sm font-medium text-center">
                  {error}
                </p>
              </div>
            )}

            <button
              onClick={verifyAndEnable}
              disabled={loading || verifyToken.length !== 6}
              className="w-full px-6 py-4 rounded-xl bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-bold text-lg hover:shadow-lg hover:shadow-primary/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Verifying..." : "Enable 2FA"}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Success */}
      {step === 3 && (
        <div className="max-w-2xl mx-auto text-center py-8">
          <div className="w-24 h-24 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center mx-auto mb-6 animate-bounce">
            <svg
              className="w-12 h-12 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            2FA Enabled Successfully!
          </h2>
          <p className="text-[#A1A1AA] text-lg mb-6">
            Your account is now protected with two-factor authentication.
          </p>
          <div className="p-4 bg-surface-soft rounded-xl border border-[#1F2937] inline-block">
            <p className="text-sm text-[#A1A1AA]">
              You'll need your authenticator app to sign in from now on.
            </p>
          </div>
        </div>
      )}
    </Card>
  );
};

export default TwoFASetup;
